// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Card, Button, Space, Tag, Typography, Avatar, Divider, Spin, message, Collapse, Badge, Popconfirm, Dropdown, Checkbox } from 'antd'
import type { MenuProps } from 'antd'
import MaterialIcon from '../components/MaterialIcon'
import { useEmailThread } from '../hooks/useEmailThread'
import { useLabels } from '../hooks/useLabels'
import EmailComposer from '../components/EmailComposer'
import AIResponseSuggestions from '../components/AIResponseSuggestions'
import CreateCustomerModal from '../components/CreateCustomerModal'
import CreateEstimateModal from '../components/CreateEstimateModal'
import CreateJobModal from '../components/CreateJobModal'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Panel } = Collapse

const EmailThread: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>()
  const [searchParams] = useSearchParams()
  const senderEmail = searchParams.get('sender') || undefined
  const navigate = useNavigate()
  const { thread, messages, isLoading, sendEmail, isSending, markAsRead, deleteThread, isDeleting, updateLabels, isUpdatingLabels } = useEmailThread(threadId, senderEmail)
  const { userLabels, isLoading: labelsLoading } = useLabels()
  const [showComposer, setShowComposer] = useState(false)
  const [showAISuggestions, setShowAISuggestions] = useState(false)
  const [expandedMessages, setExpandedMessages] = useState<string[]>([])
  const [aiGeneratedResponse, setAiGeneratedResponse] = useState<string>('')
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showEstimateModal, setShowEstimateModal] = useState(false)
  const [showJobModal, setShowJobModal] = useState(false)
  const [createdCustomer, setCreatedCustomer] = useState<any>(null)

  // Auto-expand the most recent message (now first in array since sorted newest first)
  useEffect(() => {
    if (messages && messages.length > 0) {
      setExpandedMessages([messages[0].id])
    }
  }, [messages])

  // Mark as read when viewing
  useEffect(() => {
    if (thread && thread.isUnread) {
      markAsRead(true)
    }
  }, [thread])

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading conversation...</div>
      </div>
    )
  }

  if (!thread) {
    return <div>Thread not found</div>
  }

  const lastMessage = messages[0] // Newest message is first since sorted newest first

  const handleSendReply = async (body: string) => {
    if (!lastMessage) return

    sendEmail({
      to: thread.fromEmail,
      subject: thread.subject.startsWith('Re:') ? thread.subject : `Re: ${thread.subject}`,
      body,
      threadId: thread.id,
      inReplyTo: lastMessage.id,
    })

    setShowComposer(false)
    setAiGeneratedResponse('') // Clear AI response after sending
    message.success('Reply sent!')
  }

  const handleDeleteThread = () => {
    deleteThread()
    // Navigate back to inbox after deletion
    navigate('/')
  }

  const handleLabelToggle = (labelId: string) => {
    if (!thread) return
    
    const hasLabel = thread.labels.includes(labelId)
    if (hasLabel) {
      updateLabels({ remove: [labelId] })
    } else {
      updateLabels({ add: [labelId] })
    }
  }

  // Build labels menu items
  const labelsMenuItems: MenuProps['items'] = userLabels.map((label) => ({
    key: label.id,
    label: (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox 
          checked={thread?.labels?.includes(label.id)}
          onChange={() => handleLabelToggle(label.id)}
          disabled={isUpdatingLabels}
        >
          {label.name}
        </Checkbox>
      </div>
    ),
  }))

  const renderMessageHeader = (msg: any, index: number) => {
    const isExpanded = expandedMessages.includes(msg.id)
    const isLatest = index === 0 // Latest message is now first in array
    
    // Determine if this is an incoming or outgoing message
    const businessEmail = thread.customer ? 'hello@serviceflow-pro.com' : thread.fromEmail
    const isIncoming = msg.from.includes(thread.fromEmail)
    const isOutgoing = msg.from.includes(businessEmail)
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%',
        padding: '8px 0'
      }}>
        <Space size="middle" style={{ flex: 1 }}>
          <Avatar 
            icon={<MaterialIcon icon="person" style={{ fontSize: '24px' }} />} 
            style={{ 
              backgroundColor: isLatest ? '#52c41a' : isIncoming ? '#1890ff' : '#722ed1',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} 
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Space size="small" style={{ flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: '15px', color: isIncoming ? '#1890ff' : '#722ed1' }}>
                  {isIncoming ? '←' : '→'} {isIncoming ? 'From:' : 'To:'}
                </Text>
                <Text strong style={{ fontSize: '15px' }}>{msg.from}</Text>
              </Space>
              {isLatest && (
                <Tag color="green">Latest</Tag>
              )}
              {isIncoming && (
                <Tag color="blue">Received</Tag>
              )}
              {isOutgoing && (
                <Tag color="purple">Sent</Tag>
              )}
            </div>
            <div style={{ marginTop: '4px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                To: {msg.to}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '16px' }}>
                {dayjs(msg.date).format('MMM D, YYYY [at] h:mm A')}
              </Text>
            </div>
          </div>
        </Space>
        {!isExpanded && msg.snippet && (
          <div style={{ flex: 1, marginLeft: '24px', marginRight: '24px', overflow: 'hidden' }}>
            <Text type="secondary" ellipsis style={{ fontSize: '13px' }}>
              {msg.snippet.replace(/<[^>]*>/g, '').substring(0, 100)}...
            </Text>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<MaterialIcon icon="arrow_back" style={{ fontSize: '18px' }} />}
            onClick={() => navigate('/')}
            type="text"
          >
            Back to Inbox
          </Button>

          <div>
            <Title level={3} style={{ margin: '8px 0' }}>
              {thread.subject}
            </Title>
            <Space wrap>
              <Tag icon={<MaterialIcon icon="mail" style={{ fontSize: '14px' }} />}>
                {messages.length} message{messages.length > 1 ? 's' : ''}
              </Tag>
              {thread.customer && (
                <Tag color="blue" icon={<MaterialIcon icon="person" style={{ fontSize: '14px' }} />}>
                  {thread.customer.firstName} {thread.customer.lastName}
                </Tag>
              )}
              {thread.job && (
                <Tag color="green">Job #{thread.job.id}</Tag>
              )}
            </Space>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <Space wrap>
            <Button
              type="primary"
              icon={<MaterialIcon icon="reply" style={{ fontSize: '18px' }} />}
              onClick={() => setShowComposer(!showComposer)}
            >
              Reply
            </Button>
            <Button
              icon={<MaterialIcon icon="auto_awesome" style={{ fontSize: '18px' }} />}
              onClick={() => setShowAISuggestions(!showAISuggestions)}
            >
              AI Suggestions
            </Button>
            
            <Divider type="vertical" />
            
            {/* Customer Actions */}
            {!thread.customer && !createdCustomer ? (
              <Button
                icon={<MaterialIcon icon="person_add" style={{ fontSize: '18px' }} />}
                onClick={() => setShowCustomerModal(true)}
                style={{ borderColor: '#52c41a', color: '#52c41a' }}
              >
                Create Customer
              </Button>
            ) : (
              <Button
                icon={<MaterialIcon icon="check_circle" style={{ fontSize: '18px' }} />}
                disabled
                type="default"
                style={{ 
                  color: '#52c41a', 
                  borderColor: '#52c41a',
                  cursor: 'not-allowed',
                  opacity: 0.6
                }}
              >
                Customer Created
              </Button>
            )}
            
            {/* Job Actions */}
            {(thread.customer || createdCustomer) && (
              <Button
                type="primary"
                icon={<MaterialIcon icon="work" style={{ fontSize: '18px' }} />}
                onClick={() => setShowJobModal(true)}
              >
                Create Job
              </Button>
            )}

            {/* Estimate Actions */}
            {(thread.customer || createdCustomer) && (
              <Button
                icon={<MaterialIcon icon="description" style={{ fontSize: '18px' }} />}
                onClick={() => setShowEstimateModal(true)}
                style={{ borderColor: '#1890ff', color: '#1890ff' }}
              >
                Create Estimate
              </Button>
            )}

            <Divider type="vertical" />

            {/* Labels Action */}
            <Dropdown
              menu={{ items: labelsMenuItems }}
              trigger={['click']}
              disabled={labelsLoading || userLabels.length === 0}
            >
              <Button
                icon={<MaterialIcon icon="label" style={{ fontSize: '18px' }} />}
                loading={isUpdatingLabels}
              >
                Labels
              </Button>
            </Dropdown>

            {/* Delete Action */}
            <Popconfirm
              title="Move to trash?"
              description="This email will be moved to your Gmail trash."
              onConfirm={handleDeleteThread}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<MaterialIcon icon="delete" style={{ fontSize: '18px' }} />}
                loading={isDeleting}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      </Card>

      {/* AI Suggestions */}
      {showAISuggestions && lastMessage && (
        <AIResponseSuggestions
          threadId={thread.id}
          messageId={lastMessage.id}
          onSelectResponse={(response) => {
            setAiGeneratedResponse(response)
            setShowComposer(true)
            setShowAISuggestions(false)
          }}
          onAddProducts={(products) => {
            // Format products as quote HTML and add to composer
            const formatProductsAsQuote = (items: any[]) => {
              const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0)
              const tax = subtotal * 0.08
              const total = subtotal + tax

              // Create hidden metadata with item IDs for easy extraction later
              const metadata = {
                type: 'ai_recommended_quote',
                timestamp: new Date().toISOString(),
                items: items.map(item => ({
                  id: item.id,
                  name: item.name,
                  description: item.description,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                  subtotal: item.subtotal,
                })),
                subtotal,
                tax,
                total,
              }

              const itemsList = items.map((item, idx) => {
                const description = item.description 
                  ? `<div style="margin-left: 20px; color: #666; font-size: 13px; font-style: italic; margin-top: 4px; margin-bottom: 8px;">${item.description}</div>`
                  : '<br/>'
                
                return `
                  <div style="padding: 12px; background: white; border-radius: 6px; margin-bottom: 10px; border-left: 4px solid #faad14;">
                    <div style="display: flex; justify-content: space-between; align-items: start;">
                      <div style="flex: 1;">
                        <strong style="font-size: 15px; color: #faad14;">⭐ ${idx + 1}. ${item.name}</strong>
                        ${description}
                      </div>
                      <div style="text-align: right; margin-left: 20px;">
                        <div style="color: #666; font-size: 13px;">Qty: ${item.quantity} × $${item.unitPrice.toFixed(2)}</div>
                        <div style="font-size: 18px; font-weight: bold; color: #faad14; margin-top: 4px;">$${item.subtotal.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                `
              }).join('')

              return `
                <!-- QUOTE_METADATA:${btoa(JSON.stringify(metadata))} -->
                <div style="margin: 24px 0; padding: 24px; border: 2px solid #faad14; border-radius: 12px; background: linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                  
                  <div style="text-align: center; margin-bottom: 20px;">
                    <h2 style="margin: 0; color: #faad14; font-size: 24px; font-weight: 600;">
                      🌟 AI Recommended Products
                    </h2>
                    <div style="height: 3px; width: 80px; background: #faad14; margin: 12px auto 0;"></div>
                    <div style="margin-top: 8px; color: #d48806; font-size: 13px; font-style: italic;">
                      Powered by Artificial Intelligence
                    </div>
                  </div>

                  <div style="background: #fafafa; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                    <div style="color: #d48806; font-size: 14px; font-weight: 500; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Recommended Items</div>
                    ${itemsList}
                  </div>

                  <div style="background: white; padding: 20px; border-radius: 8px; border: 1px solid #ffd591;">
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #fff7e6;">
                      <span style="color: #666; font-size: 15px;">Subtotal:</span>
                      <span style="font-size: 16px; font-weight: 500;">$${subtotal.toFixed(2)}</span>
                    </div>
                    
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #fff7e6;">
                      <span style="color: #666; font-size: 15px;">Tax (8%):</span>
                      <span style="font-size: 16px; font-weight: 500;">$${tax.toFixed(2)}</span>
                    </div>
                    
                    <div style="display: flex; justify-content: space-between; padding: 16px 0 0 0; margin-top: 8px; border-top: 2px solid #faad14;">
                      <span style="color: #faad14; font-size: 18px; font-weight: 600;">TOTAL:</span>
                      <span style="color: #faad14; font-size: 24px; font-weight: bold;">$${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div style="margin-top: 20px; padding: 16px; background: #f0f7ff; border-left: 4px solid #1890ff; border-radius: 4px;">
                    <div style="color: #666; font-size: 13px; line-height: 1.6;">
                      <strong style="color: #1890ff;">✨ AI Magic:</strong> These products were intelligently matched to your inquiry. Quote valid for <strong>30 days</strong>.
                    </div>
                  </div>

                </div>
                <p><br/></p>
              `
            }

            const quoteHtml = formatProductsAsQuote(products)
            setAiGeneratedResponse((prev) => prev ? prev + '\n\n' + quoteHtml : quoteHtml)
            setShowComposer(true)
            message.success(`Added ${products.length} AI-recommended products to email!`)
          }}
          onClose={() => setShowAISuggestions(false)}
        />
      )}

      {/* Email Composer */}
      {showComposer && (
        <Card style={{ marginBottom: '24px' }}>
          <EmailComposer
            onSend={handleSendReply}
            onCancel={() => {
              setShowComposer(false)
              setAiGeneratedResponse('') // Clear AI response when closing
            }}
            isSending={isSending}
            initialValue={aiGeneratedResponse}
          />
        </Card>
      )}

      {/* Messages - Collapsible */}
      <Card 
        title={
          <Space>
            <MaterialIcon icon="forum" style={{ fontSize: '22px', color: '#1890ff' }} />
            <span style={{ fontSize: '16px', fontWeight: 600 }}>Conversation History</span>
            <Badge 
              count={messages.length} 
              style={{ backgroundColor: '#52c41a' }}
            />
          </Space>
        }
        style={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: '12px'
        }}
      >
        <div style={{ padding: '8px 0' }}>
          <Collapse 
            activeKey={expandedMessages}
            onChange={(keys) => setExpandedMessages(keys as string[])}
            expandIconPosition="end"
            bordered={false}
            style={{ background: 'transparent' }}
          >
            {messages.map((msg, index) => {
              const isLatest = index === 0 // Latest message is first since sorted newest first
              const isExpanded = expandedMessages.includes(msg.id)
              
              // Determine message direction
              const isIncoming = msg.from.includes(thread.fromEmail)
              const isOutgoing = !isIncoming
              
              return (
                <Panel
                  header={renderMessageHeader(msg, index)}
                  key={msg.id}
                  style={{
                    marginBottom: '16px',
                    border: isLatest 
                      ? '2px solid #52c41a' 
                      : isIncoming 
                        ? '1px solid #91d5ff'
                        : '1px solid #d3adf7',
                    borderRadius: '12px',
                    background: isLatest 
                      ? '#f6ffed' 
                      : isIncoming 
                        ? '#e6f7ff'
                        : '#f9f0ff',
                    boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s',
                    overflow: 'hidden',
                  }}
                  className="email-message-panel"
                >
                  <Divider style={{ margin: '0 0 20px 0' }} />
                  
                  <div style={{ padding: '0 16px 16px 56px' }}>
                    {/* Email Body */}
                    <div 
                      style={{ 
                        fontSize: '14px', 
                        lineHeight: '1.8',
                        color: '#262626',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                      }}
                      dangerouslySetInnerHTML={{ __html: msg.body || msg.snippet || 'No content' }}
                    />

                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div 
                        style={{ 
                          marginTop: '24px',
                          padding: '16px',
                          background: '#fafafa',
                          borderRadius: '8px',
                          border: '1px solid #f0f0f0'
                        }}
                      >
                        <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                          <MaterialIcon icon="attach_file" style={{ marginRight: '8px', fontSize: '18px' }} />
                          Attachments ({msg.attachments.length})
                        </Text>
                        <Space wrap>
                          {msg.attachments.map((att: any, i: number) => (
                            <Tag 
                              key={i} 
                              icon={<MaterialIcon icon="description" style={{ fontSize: '14px' }} />}
                              style={{ 
                                padding: '6px 12px',
                                fontSize: '13px',
                                borderRadius: '6px'
                              }}
                            >
                              {att.filename} ({att.size})
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                  </div>
                </Panel>
              )
            })}
          </Collapse>

          {/* Load More Messages */}
          {messages.length > 10 && (
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Button type="dashed" size="large">
                Load older messages ({messages.length - 10} more)
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Create Customer Modal */}
      {threadId && (
        <CreateCustomerModal
          visible={showCustomerModal}
          threadId={threadId}
          senderEmail={senderEmail} // Pass sender email for grouped conversations
          onClose={() => setShowCustomerModal(false)}
          onSuccess={(customer) => {
            setCreatedCustomer(customer)
            message.success(`Customer ${customer.firstName} ${customer.lastName} created successfully!`)
            // Optionally show estimate modal right after
            setTimeout(() => setShowEstimateModal(true), 500)
          }}
        />
      )}

      {/* Create Estimate Modal */}
      {threadId && (thread.customer || createdCustomer) && (
        <CreateEstimateModal
          visible={showEstimateModal}
          threadId={threadId}
          customerId={thread.customer?.id || createdCustomer?.id}
          customerName={
            thread.customer 
              ? `${thread.customer.firstName} ${thread.customer.lastName}`
              : createdCustomer 
                ? `${createdCustomer.firstName} ${createdCustomer.lastName}`
                : 'Customer'
          }
          customerData={{
            address: thread.customer?.address || createdCustomer?.address,
            city: thread.customer?.city || createdCustomer?.city,
            state: thread.customer?.state || createdCustomer?.state,
            zipCode: thread.customer?.zipCode || createdCustomer?.zipCode,
          }}
          senderEmail={senderEmail}
          onClose={() => setShowEstimateModal(false)}
          onSuccess={(estimate) => {
            message.success(`Estimate #${estimate.estimateNumber} created successfully!`)
          }}
        />
      )}

      {/* Create Job Modal */}
      {showJobModal && (
        <CreateJobModal
          visible={showJobModal}
          threadId={threadId}
          customerId={thread.customer?.id || createdCustomer?.id}
          senderEmail={senderEmail}
          onClose={() => setShowJobModal(false)}
        />
      )}
    </div>
  )
}

export default EmailThread

